import React, { useState } from "react";
import { Box, IconButton, TextField, Tooltip } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import "./AiChatAssistant.scss";

interface ChatMessage {
  id: string;
  role: "assistant" | "user";
  text: string;
}

const QUICK_PROMPTS = [
  "Find wellness partners",
  "Explain my benefits",
  "Book a doctor consult",
];

const INITIAL_MESSAGES: ChatMessage[] = [
  {
    id: "welcome",
    role: "assistant",
    text: "Hi, I am your EB assistant. Ask about marketplace partners, benefits, claims, or wellness services.",
  },
];

const AiChatAssistant: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>(INITIAL_MESSAGES);

  const handleSend = (text?: string) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed) return;

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      text: trimmed,
    };

    const assistantMessage: ChatMessage = {
      id: `assistant-${Date.now()}`,
      role: "assistant",
      text: "Thanks for your question. AI responses will connect here soon — for now, browse Marketplace or Support for help.",
    };

    setMessages((prev) => [...prev, userMessage, assistantMessage]);
    setInput("");
  };

  return (
    <Box className={`ai_chat_assistant ${open ? "open" : ""}`}>
      {open && (
        <Box className="ai_chat_panel">
          <Box className="ai_chat_header">
            <Box className="ai_chat_title">
              <SmartToyOutlinedIcon />
              <Box>
                <strong>AI Assistant</strong>
                <span>Employee benefits help</span>
              </Box>
            </Box>
            <IconButton size="small" onClick={() => setOpen(false)} aria-label="Close chat">
              <CloseRoundedIcon />
            </IconButton>
          </Box>

          <Box className="ai_chat_messages">
            {messages.map((message) => (
              <Box
                key={message.id}
                className={`ai_chat_message ${message.role === "user" ? "user" : "assistant"}`}
              >
                {message.text}
              </Box>
            ))}
          </Box>

          <Box className="ai_chat_prompts">
            {QUICK_PROMPTS.map((prompt) => (
              <button key={prompt} type="button" onClick={() => handleSend(prompt)}>
                {prompt}
              </button>
            ))}
          </Box>

          <Box className="ai_chat_input_row">
            <TextField
              fullWidth
              size="small"
              placeholder="Ask anything about your benefits..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSend();
                }
              }}
            />
            <IconButton className="ai_chat_send" onClick={() => handleSend()} aria-label="Send message">
              <SendRoundedIcon />
            </IconButton>
          </Box>
        </Box>
      )}

      <Tooltip title={open ? "Close assistant" : "AI Assistant"} placement="left">
        <button
          type="button"
          className="ai_chat_fab"
          onClick={() => setOpen((prev) => !prev)}
          aria-label={open ? "Close AI assistant" : "Open AI assistant"}
        >
          {open ? <CloseRoundedIcon /> : <SmartToyOutlinedIcon />}
        </button>
      </Tooltip>
    </Box>
  );
};

export default AiChatAssistant;
